import { Context, Data, Effect, Layer } from 'effect';

const TypeId = 'okrapdf/core/persistence/BlobStore/TypeId' as const;
const ErrorTypeId = 'okrapdf/core/persistence/BlobStore/BlobStoreError' as const;

export interface BlobMetadata {
  readonly key: string;
  readonly contentType: string;
  readonly size: number;
}

export interface BlobObject extends BlobMetadata {
  readonly bytes: Uint8Array;
}

export interface BlobStore {
  readonly [TypeId]: typeof TypeId;
  readonly put: (
    key: string,
    bytes: Uint8Array,
    contentType: string,
  ) => Effect.Effect<void, BlobStoreError>;
  readonly get: (key: string) => Effect.Effect<BlobObject | undefined, BlobStoreError>;
  readonly head: (key: string) => Effect.Effect<BlobMetadata | undefined, BlobStoreError>;
  readonly delete: (key: string) => Effect.Effect<void, BlobStoreError>;
  readonly list: (prefix?: string) => Effect.Effect<ReadonlyArray<BlobMetadata>, BlobStoreError>;
}

export type BlobStoreMethod = 'put' | 'get' | 'head' | 'delete' | 'list';

export class BlobStoreError extends Data.TaggedError('BlobStoreError')<{
  readonly message: string;
  readonly method: BlobStoreMethod;
  readonly key?: string;
  readonly cause?: unknown;
}> {
  readonly [ErrorTypeId]: typeof ErrorTypeId = ErrorTypeId;
}

export const BlobStore: Context.Service<BlobStore, BlobStore> = Context.Service(
  'okrapdf/core/persistence/BlobStore',
);

export interface BlobStoreMakeOptions {
  readonly put: (
    key: string,
    bytes: Uint8Array,
    contentType: string,
  ) => Effect.Effect<void, BlobStoreError>;
  readonly get: (key: string) => Effect.Effect<BlobObject | undefined, BlobStoreError>;
  readonly delete: (key: string) => Effect.Effect<void, BlobStoreError>;
  readonly list: (prefix?: string) => Effect.Effect<ReadonlyArray<BlobMetadata>, BlobStoreError>;
}

function invalidKey(method: BlobStoreMethod, key: string): BlobStoreError {
  return new BlobStoreError({
    method,
    key,
    message: 'Blob keys must not be empty',
  });
}

function withKey<A>(
  method: BlobStoreMethod,
  key: string,
  effect: () => Effect.Effect<A, BlobStoreError>,
): Effect.Effect<A, BlobStoreError> {
  return key.length === 0 ? Effect.fail(invalidKey(method, key)) : Effect.suspend(effect);
}

/** Construct a BlobStore from the four backend primitives. */
export function make(options: BlobStoreMakeOptions): BlobStore {
  const get = (key: string) => withKey('get', key, () => options.get(key));

  return BlobStore.of({
    [TypeId]: TypeId,
    put: (key, bytes, contentType) => withKey('put', key, () => options.put(key, bytes, contentType)),
    get,
    head: (key) =>
      withKey('head', key, () =>
        Effect.map(get(key), (object) =>
          object === undefined
            ? undefined
            : { key: object.key, contentType: object.contentType, size: object.size },
        ),
      ),
    delete: (key) => withKey('delete', key, () => options.delete(key)),
    list: (prefix) =>
      Effect.map(options.list(prefix), (items) =>
        [...items]
          .filter((item) => prefix === undefined || item.key.startsWith(prefix))
          .sort((left, right) => left.key.localeCompare(right.key)),
      ),
  });
}

/** Process-local BlobStore for tests and ephemeral runtimes. */
export const layerMemory: Layer.Layer<BlobStore> = Layer.sync(BlobStore)(() => {
  const objects = new Map<string, BlobObject>();

  return make({
    put: (key, bytes, contentType) =>
      Effect.sync(() => {
        const storedBytes = new Uint8Array(bytes);
        objects.set(key, {
          key,
          bytes: storedBytes,
          contentType,
          size: storedBytes.byteLength,
        });
      }),
    get: (key) =>
      Effect.sync(() => {
        const object = objects.get(key);
        return object === undefined ? undefined : { ...object, bytes: new Uint8Array(object.bytes) };
      }),
    delete: (key) =>
      Effect.sync(() => {
        objects.delete(key);
      }),
    list: () =>
      Effect.sync(() =>
        [...objects.values()].map(({ key, contentType, size }) => ({ key, contentType, size })),
      ),
  });
});
